import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/tax-returns';

// ============================================
// Tax returns and whether they are paid, from imported bank statements
// ============================================

/**
 * @param {Object} params - { company, project, year, month, status, type }
 * @returns {Promise<Object>} { items, summary, statementCoverage }
 */
export const getTaxReturns = async (params) => {
  const response = await axiosClient.get(API_BASE, { params });
  return response.data;
};

export const getTaxReturn = async (company, id) => {
  const response = await axiosClient.get(`${API_BASE}/${id}`, { params: { company } });
  return response.data;
};

/** @param {'return'|'order'} [which] - the return's own file, or the payment order page of a PDPO return */
export const getTaxReturnFileUrl = async (company, id, which = 'return') => {
  const response = await axiosClient.get(`${API_BASE}/${id}/file`, { params: { company, which } });
  return response.data.url;
};

/**
 * @param {string} company
 * @param {File[]} files - notices and bank statements, in any mix
 * @param {Object} [options] - { project, kind }
 * @returns {Promise<Object>} { results, statuses }
 */
export const importTaxDocuments = async (company, files, options = {}) => {
  const form = new FormData();
  form.append('company', company);
  if (options.project) form.append('project', options.project);
  if (options.kind) form.append('kind', options.kind);
  if (options.category) form.append('category', options.category);
  form.append('sourcePaths', JSON.stringify(files.map((f) => f.webkitRelativePath || f.name)));
  files.forEach((file) => form.append('files', file));

  const response = await axiosClient.post(`${API_BASE}/import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60 * 1000,
  });
  return response.data;
};

/**
 * Bank statement PDFs as BizniSoft's e-banking XML (one file, or a zip). Nothing is stored.
 *
 * @returns {Promise<{ blob: Blob, fileName: string, results: Object[] }>}
 */
export const convertStatementsToXml = async (company, files) => {
  const form = new FormData();
  form.append('company', company);
  files.forEach((file) => form.append('files', file));
  let response;
  try {
    response = await axiosClient.post(`${API_BASE}/statements/xml`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 10 * 60 * 1000,
    });
  } catch (error) {
    // With a blob response the error body is a blob too; read it so the message key and the per-file results reach the page.
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    throw error;
  }
  let results = [];
  try {
    results = JSON.parse(decodeURIComponent(response.headers['x-statement-results'] || '[]'));
  } catch {
    /* no per-file results */
  }
  const match = (response.headers['content-disposition'] || '').match(/filename\*=UTF-8''([^;]+)/);
  return { blob: response.data, fileName: match ? decodeURIComponent(match[1]) : 'izvod.xml', results };
};

export const updateTaxReturn = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/${id}`, { company, ...data });
  return response.data;
};

/** `status: null` hands the return back to the automatic matching. */
export const overrideTaxReturnStatus = async (company, id, { status, reason }) => {
  const response = await axiosClient.patch(`${API_BASE}/${id}/status`, { company, status, reason });
  return response.data;
};

/** Late-payment interest typed from the tax portal; `interest: null` brings the estimate back. */
export const setTaxReturnInterest = async (company, id, { interest, reason }) => {
  const response = await axiosClient.patch(`${API_BASE}/${id}/interest`, { company, interest, reason });
  return response.data;
};

/** NBS reference rates, one list for all companies. */
export const getInterestRates = async () => {
  const response = await axiosClient.get(`${API_BASE}/interest-rates`);
  return response.data;
};

export const createInterestRate = async (data) => {
  const response = await axiosClient.post(`${API_BASE}/interest-rates`, data);
  return response.data;
};

export const updateInterestRate = async (id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/interest-rates/${id}`, data);
  return response.data;
};

export const deleteInterestRate = async (id) => {
  const response = await axiosClient.delete(`${API_BASE}/interest-rates/${id}`);
  return response.data;
};

export const deleteTaxReturn = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/${id}`, { params: { company } });
};

export const getBankAccounts = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/bank-accounts`, { params: { company } });
  return response.data.items;
};

/** One page of the accounts for the accounts tab: `{ items, count }`. */
export const getBankAccountsPage = async (company, params) => {
  const response = await axiosClient.get(`${API_BASE}/bank-accounts`, { params: { company, ...params } });
  return response.data;
};

export const setBankAccountProject = async (company, account, project) => {
  const response = await axiosClient.patch(`${API_BASE}/bank-accounts/${account}`, { company, project });
  return response.data.items;
};

/**
 * @param {string} company
 * @param {Object} filters - the list filters
 * @param {Object} labels - column and status names in the viewer's language
 * @returns {Promise<Blob>}
 */
export const exportTaxReturns = async (company, filters, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/export`,
    { company, filters, labels },
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Accounts and folder offered for the next AB journal: the last used ones, else the defaults.
 * With `ids`, also the recipients of those returns and the AB partner code each one matches.
 */
export const getAbSettings = async (company, ids = []) => {
  const params = ids.length ? { company, ids: ids.join(',') } : { company };
  const response = await axiosClient.get(`${API_BASE}/ab-settings`, { params });
  return response.data;
};

/**
 * Journal file for AB Soft booking the chosen PDPO returns.
 *
 * @param {Object} data - { company, ids, folder, date (YYYY-MM-DD), accounts, partners: { name: code } }
 * @returns {Promise<{ blob: Blob, count: number, warnings: string[] }>}
 */
export const exportAbJournal = async (data) => {
  let response;
  try {
    response = await axiosClient.post(`${API_BASE}/ab-journal`, data, { responseType: 'blob' });
  } catch (error) {
    // With a blob response the error body is a blob too; read it so the message key reaches the page.
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    throw error;
  }
  let warnings = [];
  try {
    warnings = JSON.parse(decodeURIComponent(response.headers['x-journal-warnings'] || '%5B%5D'));
  } catch {
    warnings = [];
  }
  return { blob: response.data, count: Number(response.headers['x-journal-count']) || 0, warnings };
};

// ============================================
// Withholding tax calculations (rent, other income)
// ============================================

/** @param {Object} params - { company, project, year, month, kind, status } */
export const getCalculations = async (params) => {
  const response = await axiosClient.get(`${API_BASE}/calculations`, { params });
  return response.data;
};

export const getCalculation = async (company, id) => {
  const response = await axiosClient.get(`${API_BASE}/calculations/${id}`, { params: { company } });
  return response.data;
};

export const createCalculation = async (company, data) => {
  const response = await axiosClient.post(`${API_BASE}/calculations`, { company, ...data });
  return response.data;
};

export const updateCalculation = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/calculations/${id}`, { company, ...data });
  return response.data;
};

/** Done with the return's BOP and UID, or `done: false` to reopen it. */
export const setCalculationDone = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/calculations/${id}/done`, { company, ...data });
  return response.data;
};

export const deleteCalculation = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/calculations/${id}`, { params: { company } });
};

export const exportCalculationOrders = async (company, filters, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/calculations/export`,
    { company, filters, labels },
    { responseType: 'blob' }
  );
  return response.data;
};

/** NBS middle rate; `rate` is null when the service has none for the day. */
/** The month's calculations at once: one per ticked property (rent), or one for the ticked people (other income). */
export const createCalculationsBatch = async (company, data) => {
  const response = await axiosClient.post(`${API_BASE}/calculations/batch`, { company, ...data });
  return response.data;
};

/** Payer data and the municipality code, phone, e-mail and SVP per kind used last time, for the PPP-PD XML; with a calculation also its recipients' `names`. */
export const getPppdSettings = async (company, calculation) => {
  const response = await axiosClient.get(`${API_BASE}/pppd-settings`, { params: { company, calculation } });
  return response.data;
};

/** Treasury municipality codes for the PPP-PD return: [{ code, name }]. */
export const getMunicipalities = async () => {
  const response = await axiosClient.get(`${API_BASE}/municipalities`);
  return response.data;
};

/**
 * PPP-PD XML of a calculation, for „Učitaj XML" in ePorezi.
 *
 * @param {Object} data - { paymentDate (YYYY-MM-DD), incomeCode, municipalityCode, phone, email, names: [{ surname, givenName, municipalityCode }] }
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export const exportCalculationPppdXml = async (company, id, data) => {
  let response;
  try {
    response = await axiosClient.post(`${API_BASE}/calculations/${id}/pppd-xml`, { company, ...data }, { responseType: 'blob' });
  } catch (error) {
    // With a blob response the error body is a blob too; read it so the message key reaches the page.
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    throw error;
  }
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
  return { blob: response.data, fileName: match ? decodeURIComponent(match[1]) : 'PPP-PD.xml' };
};

/** A blob request whose error body is read back as JSON, so the message key reaches the page. */
const getBlob = async (url, params) => {
  try {
    return await axiosClient.get(url, { params, responseType: 'blob' });
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    throw error;
  }
};

const fileNameOf = (response, fallback) => {
  const match = (response.headers['content-disposition'] || '').match(/filename\*=UTF-8''([^;]+)/);
  return match ? decodeURIComponent(match[1]) : fallback;
};

/** One calculation as a workbook, in the page's language. */
export const exportCalculationWorkbook = async (company, id, language) => {
  const response = await getBlob(`${API_BASE}/calculations/${id}/excel`, { company, language });
  return { blob: response.data, fileName: fileNameOf(response, 'obracun.xlsx') };
};

/** AB journal of a finished rent calculation; `missingPartners` are landlords booked without an AB code. */
export const exportCalculationAbJournal = async (company, id) => {
  const response = await getBlob(`${API_BASE}/calculations/${id}/ab-journal`, { company });
  const missing = decodeURIComponent(response.headers['x-missing-partners'] || '');
  return { blob: response.data, fileName: fileNameOf(response, 'NALOG.xls'), missingPartners: missing ? missing.split('|') : [] };
};

/** Who is mailed when a calculation is done, and the rent journal's AB accounts and folder. */
export const getCalculationSettings = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/calculation-settings`, { params: { company } });
  return response.data;
};

export const updateCalculationSettings = async (company, data) => {
  const response = await axiosClient.patch(`${API_BASE}/calculation-settings`, { company, ...data });
  return response.data;
};

// ============================================
// People paid rent or other income, and rented properties
// ============================================

/** `{ items, count, total }`: one page with `offset`/`limit`, else every active one (all with `inactive`). */
export const getRecipients = async (company, params = {}) => {
  const response = await axiosClient.get(`${API_BASE}/recipients`, { params: { company, ...params } });
  return response.data;
};

export const createRecipient = async (company, data) => {
  const response = await axiosClient.post(`${API_BASE}/recipients`, { company, ...data });
  return response.data;
};

export const updateRecipient = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/recipients/${id}`, { company, ...data });
  return response.data;
};

export const deleteRecipient = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/recipients/${id}`, { params: { company } });
};

/**
 * What a sheet of people (name, JMBG / passport, net) would bring into a calculation; nothing is written.
 * @param {File} file - .xlsx, .xls or .csv
 * @param {Object|null} mapping - roles picked by hand: { name: 0, net: 3, ... }
 * @returns {Promise<Object>} { columns, roles, detected, mapping, rows, skipped, headerRow }
 */
export const previewRecipientSheet = async (company, file, mapping = null) => {
  const form = new FormData();
  form.append('company', company);
  if (mapping) form.append('mapping', JSON.stringify(mapping));
  form.append('file', file);
  const response = await axiosClient.post(`${API_BASE}/calculations/recipient-sheet/preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Empty sheet with the columns the import reads, headed in the page's language (Serbian, else English). */
export const getRecipientSheetTemplate = async (language) => {
  const response = await getBlob(`${API_BASE}/calculations/recipient-sheet/template`, { language });
  return { blob: response.data, fileName: fileNameOf(response, 'sablon-primaoci-prihoda.xlsx') };
};

/**
 * Puts a sheet's confirmed people into the list: linked by JMBG / passport when already there, entered otherwise.
 * @returns {Promise<Object>} { items: [{ recipient, name, idNumber, address, account, accountHolder }], created, linked }
 */
export const importRecipients = async (company, kind, people) => {
  const response = await axiosClient.post(`${API_BASE}/recipients/import`, { company, kind, people });
  return response.data;
};

/** `{ items, count, total }`: one page with `offset`/`limit`, else every active one (all with `inactive`). */
export const getProperties = async (company, params = {}) => {
  const response = await axiosClient.get(`${API_BASE}/properties`, { params: { company, ...params } });
  return response.data;
};

export const createProperty = async (company, data) => {
  const response = await axiosClient.post(`${API_BASE}/properties`, { company, ...data });
  return response.data;
};

export const updateProperty = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/properties/${id}`, { company, ...data });
  return response.data;
};

export const deleteProperty = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/properties/${id}`, { params: { company } });
};

export const getExchangeRate = async (currency, date) => {
  const response = await axiosClient.get(`${API_BASE}/exchange-rate`, { params: { currency, date } });
  return response.data;
};

// ============================================
// Google Drive as a source of notices and statements
// ============================================

export const getDriveStatus = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/drive`, { params: { company } });
  return response.data;
};

/** Google consent address; the page sends the browser there. */
export const getDriveConnectUrl = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/drive/connect`, { params: { company } });
  return response.data.url;
};

/** Folders shared with the connected account, or the subfolders of `parent`. */
export const getDriveFolders = async (company, parent) => {
  const response = await axiosClient.get(`${API_BASE}/drive/folders`, { params: { company, parent } });
  return response.data;
};

export const updateDriveSettings = async (company, data) => {
  const response = await axiosClient.patch(`${API_BASE}/drive`, { company, ...data });
  return response.data;
};

export const startDriveSync = async (company) => {
  const response = await axiosClient.post(`${API_BASE}/drive/sync`, { company });
  return response.data;
};

export const disconnectDrive = async (company) => {
  const response = await axiosClient.delete(`${API_BASE}/drive`, { params: { company } });
  return response.data;
};

// ============================================
// A network folder of statements, sent by the AB Soft workstation
// ============================================

export const getShareStatus = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/share`, { params: { company } });
  return response.data;
};

export const updateShareSettings = async (company, path) => {
  const response = await axiosClient.patch(`${API_BASE}/share`, { company, path });
  return response.data;
};

// ============================================
// Statements the banks mail to our mailbox
// ============================================

export const getMailboxStatus = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/mailbox`, { params: { company } });
  return response.data;
};

export const updateMailboxSettings = async (company, data) => {
  const response = await axiosClient.patch(`${API_BASE}/mailbox`, { company, ...data });
  return response.data;
};

/** Reads the mailbox now (techAdmin); the run shows in the status. */
export const startMailboxSync = async (company) => {
  const response = await axiosClient.post(`${API_BASE}/mailbox/sync`, { company });
  return response.data;
};

// ============================================
// Mails to the client's people about returns to pay
// ============================================

export const getNotificationSettings = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/notifications`, { params: { company } });
  return response.data;
};

/** @param {Object} data - { enabled, announceOpen, reminderDays, cc, ccPosted, extraRecipients } */
export const updateNotificationSettings = async (company, data) => {
  const response = await axiosClient.patch(`${API_BASE}/notifications`, { company, ...data });
  return response.data;
};

/** What a run would mail now; nothing is sent. */
export const previewNotifications = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/notifications/preview`, { params: { company } });
  return response.data;
};

/** One mail rendered: { subject, html, to, cc }. */
export const renderNotificationMail = async (company, kind, project) => {
  const response = await axiosClient.get(`${API_BASE}/notifications/preview/mail`, {
    params: { company, kind, project: project || 'none' },
  });
  return response.data;
};

export const getNotificationLog = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/notifications/log`, { params: { company } });
  return response.data.items;
};

/** Who an overdue notice would go to, and the mail itself. */
export const getOverdueNotice = async (company, id) => {
  const response = await axiosClient.get(`${API_BASE}/${id}/overdue-notice`, { params: { company } });
  return response.data;
};

/** Sends the overdue notice; `file` (a PDF, e.g. the balance statement) goes along with the payment slip. */
export const sendOverdueNotice = async (company, id, file = null) => {
  const form = new FormData();
  form.append('company', company);
  if (file) form.append('file', file);
  const response = await axiosClient.post(`${API_BASE}/${id}/overdue-notice`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
